import { atom, type SetStateAction, type WritableAtom } from "jotai"

// TODO if necessary expand with nested key support

type NonFunction<T> = [T] extends [(...args: any[]) => any] ? never : T

const globalCache = new WeakMap()

function isFunction<T>(x: T): x is T & ((...args: any[]) => any) {
  return typeof x === "function"
}
function getCached<T, U extends object, M extends WeakMap<U, T>>(
  cb: () => T,
  map: M,
  key: U
): T
function getCached<T, U extends string, M extends Map<U, T>>(
  cb: () => T,
  map: M,
  key: U
): T
function getCached<T, M extends Map<string, T> | WeakMap<object, T>>(
  cb: () => T,
  map: M,
  key: M extends Map<string, T> ? string : object
): T {
  // this makes no sense but need to keep ts happy
  return (map.has(key as never) ? map : map.set(key as never, cb())).get(
    key as never
  ) as T
}

function memo<T>(create: () => T, dep1: object, dep2: string): T {
  const atomBasedCache = getCached(
    () => new Map<string, T>(),
    globalCache,
    dep1
  )

  return getCached(create, atomBasedCache, dep2)
}

export type FocusedAtom<
  Value,
  PropName extends keyof Value,
  Result = void,
  ActualValue = Value[PropName] | Promise<Value[PropName]>,
> = WritableAtom<
  ActualValue,
  [SetStateAction<Value[PropName]>],
  ActualValue extends Promise<any> ? Promise<Result> : Result
>

export function focusAtom<Value, Result, PropName extends keyof Awaited<Value>>(
  baseAtom: WritableAtom<
    Value,
    [SetStateAction<Awaited<Value>>],
    Promise<Result>
  >,
  propertyName: PropName
): WritableAtom<
  Promise<Awaited<Value>[PropName]>,
  [SetStateAction<Awaited<Value>[PropName]>],
  Result
>
export function focusAtom<Value, Result, PropName extends keyof Value>(
  baseAtom: WritableAtom<Value, [NonFunction<Value>], Result>,
  propertyName: PropName
): WritableAtom<Value[PropName], [SetStateAction<Value[PropName]>], Result>
export function focusAtom<Value, Result, PropName extends keyof Value>(
  baseAtom: WritableAtom<
    Value | Promise<Value>,
    [NonFunction<Value | Promise<Value>>],
    Promise<Result> | Result
  >,
  propertyName: PropName
) {
  return memo(
    () => {
      const derivedAtom = atom(
        (get) => {
          const baseValue = get(baseAtom)

          return baseValue instanceof Promise
            ? baseValue.then((value) => value[propertyName])
            : (baseValue as Value)[propertyName]
        },
        (
          get,
          set,
          update: Value[PropName] | ((prev: Value[PropName]) => Value[PropName])
        ) => {
          const newValueProducer: (args: Value[PropName]) => Value[PropName] =
            isFunction(update) ? update : () => update
          const baseValue = get(baseAtom)
          if (baseValue instanceof Promise) {
            return baseValue.then((value: Value) => {
              set(baseAtom, {
                ...value,
                [propertyName]: newValueProducer(value[propertyName]),
              } as NonFunction<Promise<Value> | Value>)
            })
          }

          return set(baseAtom, {
            ...baseValue,
            [propertyName]: newValueProducer(
              (baseValue as Value)[propertyName]
            ),
          } as NonFunction<Promise<Value> | Value>)
        }
      )
      return derivedAtom
    },
    baseAtom,
    propertyName as string
  )
}
